import React, { useState, useEffect, useCallback } from 'react';
import { Key, Eye, EyeOff, Save, Check, AlertCircle, Shield, ShieldOff, DollarSign, Activity, Clock } from 'lucide-react';

const PROVIDER_LABELS = {
  anthropic: 'Anthropic (Claude)',
  openai: 'OpenAI (GPT-4o)',
  google_gemini: 'Google Gemini',
  fal: 'fal.ai',
  xai: 'xAI (Grok)',
  ltx: 'LTX Video',
  elevenlabs: 'ElevenLabs',
  luma: 'Luma AI',
  astria: 'Astria.ai',
  postmark: 'Postmark (E-Mail)',
};

export default function ApiKeysAdmin() {
  const [providers, setProviders] = useState([]);
  const [monitorLastCheck, setMonitorLastCheck] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [editingKey, setEditingKey] = useState({});
  const [editingLimit, setEditingLimit] = useState({});
  const [showKey, setShowKey] = useState({});
  const [tab, setTab] = useState('keys'); // 'keys' | 'costs' | 'alerts'

  const token = localStorage.getItem('snapshot_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchProviders = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/api-keys/providers', { headers });
      if (res.ok) {
        const data = await res.json();
        setProviders(data.providers || []);
        setMonitorLastCheck(data.monitor_last_check);
      }
    } catch (err) {
      console.error('Fehler:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/api-keys/alerts?limit=10', { headers });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
      }
    } catch (err) { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchProviders();
    fetchAlerts();
    const interval = setInterval(fetchProviders, 30000);
    return () => clearInterval(interval);
  }, [fetchProviders, fetchAlerts]);

  const saveKey = async (provider) => {
    const key = editingKey[provider];
    if (!key?.trim()) return;
    try {
      const res = await fetch(`/api/admin/api-keys/providers/${provider}`, {
        method: 'PUT', headers, body: JSON.stringify({ key: key.trim() })
      });
      if (res.ok) {
        setMessage({ type: 'success', text: `${PROVIDER_LABELS[provider] || provider} Key gespeichert!` });
        setEditingKey(prev => ({ ...prev, [provider]: '' }));
        fetchProviders();
      } else {
        const err = await res.json();
        setMessage({ type: 'error', text: err.detail || 'Fehler' });
      }
    } catch { setMessage({ type: 'error', text: 'Netzwerkfehler' }); }
  };

  const saveLimit = async (provider) => {
    const limit = editingLimit[provider];
    if (limit === undefined || limit === '') return;
    try {
      const res = await fetch(`/api/admin/api-keys/providers/${provider}`, {
        method: 'PUT', headers, body: JSON.stringify({ daily_limit_usd: parseFloat(limit) })
      });
      if (res.ok) {
        setMessage({ type: 'success', text: `Limit gespeichert` });
        setEditingLimit(prev => ({ ...prev, [provider]: undefined }));
        fetchProviders();
      }
    } catch { setMessage({ type: 'error', text: 'Fehler' }); }
  };

  const toggleProvider = async (provider) => {
    try {
      const res = await fetch(`/api/admin/api-keys/providers/${provider}/toggle`, {
        method: 'POST', headers
      });
      if (res.ok) {
        const data = await res.json();
        setMessage({ type: 'success', text: `${PROVIDER_LABELS[provider]} ${data.enabled ? 'aktiviert' : 'deaktiviert'}` });
        fetchProviders();
      }
    } catch { setMessage({ type: 'error', text: 'Fehler' }); }
  };

  const getStatusColor = (p) => {
    if (!p.enabled) return 'bg-red-500/20 border-red-500/30';
    if (p.today_pct >= 80) return 'bg-red-500/10 border-red-500/20';
    if (p.today_pct >= 50) return 'bg-yellow-500/10 border-yellow-500/20';
    return 'bg-white/5 border-white/10';
  };

  const getBarColor = (pct) => {
    if (pct >= 80) return 'bg-red-500';
    if (pct >= 50) return 'bg-yellow-500';
    return 'bg-emerald-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-[#10B981] border-t-transparent rounded-full" />
      </div>
    );
  }

  const totalToday = providers.reduce((sum, p) => sum + (p.today_cost_usd || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#10B981]/20 flex items-center justify-center">
            <Key className="w-5 h-5 text-[#10B981]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">API Verwaltung</h2>
            <p className="text-sm text-white/50">Keys, Limits & Kosten-Monitor</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-white">${totalToday.toFixed(2)}</div>
          <div className="text-xs text-white/40">Kosten heute</div>
        </div>
      </div>

      {/* Monitor Status */}
      {monitorLastCheck && (
        <div className="flex items-center gap-2 text-xs text-white/40">
          <Activity className="w-3 h-3 text-emerald-400" />
          Monitor aktiv — letzte Pruefung: {new Date(monitorLastCheck).toLocaleTimeString('de-DE')}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2">
        {[
          { id: 'keys', label: 'Keys & Limits' },
          { id: 'costs', label: 'Kosten heute' },
          { id: 'alerts', label: `Alerts${alerts.length ? ` (${alerts.length})` : ''}` },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.id
                ? 'bg-[#10B981] text-black'
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Message */}
      {message && (
        <div className={`p-3 rounded-xl flex items-center gap-2 text-sm ${
          message.type === 'success'
            ? 'bg-green-500/20 border border-green-500/30 text-green-400'
            : 'bg-red-500/20 border border-red-500/30 text-red-400'
        }`}>
          {message.type === 'success' ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      {/* TAB: Keys & Limits */}
      {tab === 'keys' && (
        <div className="space-y-3">
          {providers.map(p => (
            <div key={p.provider} className={`rounded-xl p-4 border ${getStatusColor(p)}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-white font-medium">{PROVIDER_LABELS[p.provider] || p.provider}</span>
                  {p.has_key ? (
                    <span className="text-xs text-green-400 bg-green-500/20 px-2 py-0.5 rounded-full">
                      {p.key_source === 'db' ? 'DB' : '.env'}
                    </span>
                  ) : (
                    <span className="text-xs text-red-400 bg-red-500/20 px-2 py-0.5 rounded-full">Kein Key</span>
                  )}
                  {!p.enabled && (
                    <span className="text-xs text-red-400 bg-red-500/20 px-2 py-0.5 rounded-full">
                      DEAKTIVIERT {p.disabled_reason === 'threshold_exceeded' ? '(Limit)' : '(manuell)'}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* Kosten heute */}
                  {p.today_cost_usd > 0 && (
                    <span className="text-xs text-white/50 font-mono">${p.today_cost_usd.toFixed(2)} / {p.today_calls} Calls</span>
                  )}
                  {/* Toggle */}
                  {!p.is_protected && (
                    <button
                      onClick={() => toggleProvider(p.provider)}
                      className={`p-1.5 rounded-lg transition-colors ${
                        p.enabled
                          ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                          : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                      }`}
                      title={p.enabled ? 'Deaktivieren' : 'Aktivieren'}
                    >
                      {p.enabled ? <Shield className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </div>

              {/* Spend Bar */}
              {p.daily_limit_usd > 0 && (
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-white/40 mb-1">
                    <span>${p.today_cost_usd.toFixed(2)} von ${p.daily_limit_usd.toFixed(2)}</span>
                    <span>{p.today_pct.toFixed(0)}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${getBarColor(p.today_pct)}`}
                      style={{ width: `${Math.min(p.today_pct, 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Key + Limit Inputs */}
              <div className="flex gap-2 flex-wrap">
                {/* Key Input */}
                <div className="relative flex-1 min-w-[200px]">
                  <input
                    type={showKey[p.provider] ? 'text' : 'password'}
                    value={editingKey[p.provider] || ''}
                    onChange={(e) => setEditingKey(prev => ({ ...prev, [p.provider]: e.target.value }))}
                    placeholder={p.has_key ? `Key: ${p.key_masked}` : 'API Key eingeben...'}
                    className="w-full px-3 py-1.5 pr-8 bg-black/30 border border-white/20 rounded-lg text-xs text-white placeholder-white/30 focus:border-[#10B981]/50 focus:outline-none font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey(prev => ({ ...prev, [p.provider]: !prev[p.provider] }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                  >
                    {showKey[p.provider] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </button>
                </div>
                {editingKey[p.provider]?.trim() && (
                  <button
                    onClick={() => saveKey(p.provider)}
                    className="px-3 py-1.5 bg-[#10B981] text-black rounded-lg text-xs font-medium hover:bg-[#10B981]/90 flex items-center gap-1"
                  >
                    <Save className="w-3 h-3" /> Key
                  </button>
                )}

                {/* Daily Limit */}
                <div className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3 text-white/40" />
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={editingLimit[p.provider] !== undefined ? editingLimit[p.provider] : (p.daily_limit_usd || '')}
                    onChange={(e) => setEditingLimit(prev => ({ ...prev, [p.provider]: e.target.value }))}
                    placeholder="Limit/Tag"
                    className="w-20 px-2 py-1.5 bg-black/30 border border-white/20 rounded-lg text-xs text-white focus:border-[#10B981]/50 focus:outline-none"
                  />
                  {editingLimit[p.provider] !== undefined && (
                    <button
                      onClick={() => saveLimit(p.provider)}
                      className="px-2 py-1.5 bg-blue-500 text-white rounded-lg text-xs hover:bg-blue-600"
                    >
                      <Check className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TAB: Kosten heute */}
      {tab === 'costs' && (
        <div className="space-y-3">
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="text-lg font-bold text-white mb-4">Kosten heute: ${totalToday.toFixed(2)}</div>
            {providers
              .filter(p => p.today_cost_usd > 0)
              .sort((a, b) => b.today_cost_usd - a.today_cost_usd)
              .map(p => (
                <div key={p.provider} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white">{PROVIDER_LABELS[p.provider] || p.provider}</span>
                    <span className="text-xs text-white/40">{p.today_calls} Calls</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${getBarColor(p.today_pct)}`}
                        style={{ width: `${Math.min(totalToday > 0 ? (p.today_cost_usd / totalToday * 100) : 0, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm font-mono text-white w-16 text-right">${p.today_cost_usd.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            {providers.filter(p => p.today_cost_usd > 0).length === 0 && (
              <p className="text-white/40 text-sm">Heute noch keine API-Kosten.</p>
            )}
          </div>
        </div>
      )}

      {/* TAB: Alerts */}
      {tab === 'alerts' && (
        <div className="space-y-3">
          {alerts.length === 0 ? (
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center text-white/40">
              Keine Alerts. Alles im gruenen Bereich.
            </div>
          ) : (
            alerts.map((a, i) => (
              <div key={i} className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-red-400">
                    {PROVIDER_LABELS[a.provider] || a.provider} — Limit ueberschritten
                  </span>
                  <span className="text-xs text-white/40">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {a.created_at ? new Date(a.created_at).toLocaleString('de-DE') : '-'}
                  </span>
                </div>
                <div className="text-xs text-white/50">
                  ${a.actual_usd?.toFixed(2)} von ${a.threshold_usd?.toFixed(2)} Limit
                  {a.calls_today && ` (${a.calls_today} Calls)`}
                  {' — '}{a.action_taken === 'provider_disabled' ? 'Provider deaktiviert' : a.action_taken}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
